/**
 * SourceCard — one retrieved chunk rendered as a pinnable, highlight-able card.
 *
 * Cards pulse briefly when the user hovers a matching `[[...]]` citation in
 * the deliverable panel (via the `deep:citation-hover` DOM CustomEvent fired
 * from `citation-link.tsx`), and can be hovered for a peek or clicked to
 * open the underlying document in the content sidebar — matching the
 * regular Explorer citation UX.
 */
import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Document } from "@/api/models"

import { BookOpen, FileText, Globe, Link2, Package, Pin } from "@/components/icons"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { CitationHoverCard } from "@/features/chat/components/citation-hover-card"
import { useCitationPreview } from "@/features/chat/contexts/use-citation-preview"
import { cn } from "@/lib/utils"

import { parsePage, type SourceCardData, sourceToCitation } from "./source-card-utils"

const TYPE_CONFIG: Record<
  SourceCardData["type"],
  { icon: React.ElementType; labelKey: string; badgeClass: string; rowClass: string }
> = {
  library: {
    icon: BookOpen,
    labelKey: "deepExplorer.source_filters.library",
    badgeClass: CATEGORY_TONES[0],
    rowClass: "border-l-foreground",
  },
  web: {
    icon: Globe,
    labelKey: "deepExplorer.source_filters.web",
    badgeClass: CATEGORY_TONES[4],
    rowClass: "border-l-foreground",
  },
  doc: {
    icon: FileText,
    labelKey: "deepExplorer.source_filters.doc",
    badgeClass: CATEGORY_TONES[1],
    rowClass: "border-l-foreground",
  },
  m365: {
    icon: Link2,
    labelKey: "deepExplorer.source_filters.m365",
    badgeClass: CATEGORY_TONES[2],
    rowClass: "border-l-foreground",
  },
  other: {
    icon: Package,
    labelKey: "deepExplorer.source_filters.other",
    badgeClass: CATEGORY_TONES[3],
    rowClass: "border-l-muted-foreground/40",
  },
}

interface SourceCardProps {
  source: SourceCardData
  /** If true, card is pinned (user-toggled) and stays highlighted. */
  pinned?: boolean
  onTogglePin?: (id: string) => void
  /** When true, the user is hovering a matching citation — briefly pulse. */
  highlighted?: boolean
  /**
   * Citation section (search service id / session type) — passed through to
   * `openPreview` so `getCitationFilePath` can resolve the right index route.
   * Mirrors the `citationSection` prop on the chat's <Answer /> component.
   */
  citationSection?: string
  /**
   * Library documents for the current session. Lets `openPreview` resolve
   * display names and the unmodified-original file path when the library is
   * configured for unmodified previews.
   */
  documents?: [string, Document][]
  /**
   * Override the "click to preview" behaviour. By default the card calls
   * `useCitationPreview().openPreview`, which opens the file in the main
   * content sidebar (replacing whatever else is there). The Deep Explorer
   * panel passes its own handler so previews render inline inside the
   * panel instead of replacing it.
   */
  onOpenPreview?: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string; documents?: [string, Document][] }
  ) => void
}

export function SourceCard({
  source,
  pinned = false,
  onTogglePin,
  highlighted = false,
  citationSection = "",
  documents,
  onOpenPreview,
}: SourceCardProps) {
  const { t } = useTranslation()
  const config = TYPE_CONFIG[source.type]
  const Icon = config.icon
  const cardRef = useRef<HTMLLIElement>(null)
  const [pulsing, setPulsing] = useState(false)
  const { openPreview: openPreviewFallback } = useCitationPreview()
  const openPreview = onOpenPreview ?? openPreviewFallback

  const citationString = sourceToCitation(source)
  const pageNumber = source.sourcepage ? parsePage(source.sourcepage) : null
  const canPreview = citationString !== null

  // External hover highlight — pulse when the prop flips true, then rest.
  useEffect(() => {
    if (!highlighted) return
    setPulsing(true)
    const timer = setTimeout(() => setPulsing(false), 1200)
    return () => clearTimeout(timer)
  }, [highlighted])

  // Scroll into view when highlighted (so user sees the source for a
  // citation in a long list).
  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [highlighted])

  const isWebUrl = (value: string) => value.startsWith("http://") || value.startsWith("https://")

  const handleOpen = () => {
    if (!citationString) return
    // Web sources open in a new tab — the inline preview is backed by the
    // internal document API which 404s on bare http(s) URLs.
    if (isWebUrl(citationString)) {
      if (typeof window !== "undefined") {
        window.open(citationString, "_blank", "noopener,noreferrer")
      }
      return
    }
    openPreview(citationString, citationSection, {
      chunkText: source.snippet,
      ...(documents ? { documents } : {}),
    })
  }

  // The hover-card "View in document" button calls onViewDocument directly,
  // bypassing handleOpen. Apply the same URL routing so web sources don't
  // hit the document API and render "Preview unavailable".
  const handleViewDocument: typeof openPreview = (citation, section, options) => {
    if (isWebUrl(citation)) {
      if (typeof window !== "undefined") {
        window.open(citation, "_blank", "noopener,noreferrer")
      }
      return
    }
    openPreview(citation, section, options)
  }

  const cardContent = (
    // The <li> click is a mouse convenience only; keyboard and AT users activate the
    // inner title button (its click bubbles up to the <li>'s onClick).
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
    <li
      ref={cardRef}
      data-source-id={source.id}
      className={cn(
        "group border-border bg-card relative flex flex-col gap-1.5 rounded-md border border-l-4 px-3 py-2 text-xs transition-colors",
        config.rowClass,
        pinned && "ring-primary/40 ring-2",
        (pulsing || highlighted) &&
          "bg-primary/5 ring-primary/30 shadow-sm ring-1 transition-[background-color,box-shadow]",
        canPreview && "hover:bg-accent/40 focus-within:ring-ring cursor-pointer focus-within:ring-2"
      )}
      onClick={canPreview ? handleOpen : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium",
              config.badgeClass
            )}
          >
            <Icon className="size-3" />
            {t(config.labelKey)}
          </span>
          {source.sourcepage && (
            <span className="text-muted-foreground shrink-0 text-[10px]">
              {t("deepExplorer.source_page", {
                page: source.sourcepage.replace(/[^0-9a-z-]+/gi, " ").trim(),
              })}
            </span>
          )}
          {(source.citationCount ?? 0) > 0 && (
            <span className="text-primary bg-primary/10 shrink-0 rounded px-1 text-[10px] font-medium">
              {t("deepExplorer.cited_count", { count: source.citationCount })}
            </span>
          )}
          {source.retrievedBy === "scout" && source.dispatchId && (
            <span
              className="bg-muted/60 text-muted-foreground shrink-0 rounded px-1 font-mono text-[10px]"
              title={t("deepExplorer.retrieved_by_scout", { id: source.dispatchId })}
            >
              scout#{source.dispatchId}
            </span>
          )}
          {source.retrievedBy === "worker" && source.dispatchId && (
            <span
              className="bg-muted/60 text-muted-foreground shrink-0 rounded px-1 font-mono text-[10px]"
              title={t("deepExplorer.retrieved_by_worker", { id: source.dispatchId })}
            >
              worker#{source.dispatchId}
            </span>
          )}
        </div>
        {onTogglePin && (
          <button
            aria-label={`${pinned ? t("deepExplorer.unpin_source") : t("deepExplorer.pin_source")}: ${source.title}`}
            aria-pressed={pinned}
            type="button"
            className={cn(
              "focus-visible:ring-ring -m-1 flex size-6 shrink-0 items-center justify-center rounded opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none",
              pinned && "opacity-100"
            )}
            onClick={(event) => {
              event.stopPropagation()
              onTogglePin(source.id)
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.stopPropagation()
              }
            }}
          >
            <Pin
              className={cn(
                "size-3",
                pinned ? "text-primary fill-current" : "text-muted-foreground"
              )}
            />
          </button>
        )}
      </div>
      {canPreview ? (
        <button
          className="flex w-full cursor-pointer flex-col gap-1.5 text-left focus-visible:outline-none"
          type="button"
          aria-label={t("deepExplorer.open_source_aria", {
            title: source.title,
            defaultValue: "Open source: {{title}}",
          })}
        >
          <span className="text-foreground block truncate font-medium">{source.title}</span>
          {source.snippet && (
            <span className="text-muted-foreground line-clamp-2 block leading-relaxed">
              {source.snippet}
            </span>
          )}
        </button>
      ) : (
        <>
          <p className="text-foreground truncate font-medium">{source.title}</p>
          {source.snippet && (
            <p className="text-muted-foreground line-clamp-2 leading-relaxed">{source.snippet}</p>
          )}
        </>
      )}
    </li>
  )

  if (!canPreview) return cardContent

  return (
    <CitationHoverCard
      chunkText={source.snippet ?? null}
      citation={citationString}
      citationSection={citationSection}
      documentsList={documents}
      pageNumber={pageNumber}
      title={source.title}
      onViewDocument={handleViewDocument}
    >
      {cardContent}
    </CitationHoverCard>
  )
}
